import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../supabase';

export interface OwnerContact {
  display_name: string | null;
  email: string | null;
}

export interface UseOwnerContactVM {
  /** Owner display name + email returned by `get_org_owner_contact(p_org_id)`.
   *  Both fields are `null` while loading, when the RPC errored, or when no
   *  Owner is resolvable; consumers should render the generic fallback copy
   *  (`Contact the org owner to restore access.`) in that case. */
  contact: OwnerContact;
  loading: boolean;
  /** True when the RPC failed (RLS denial or 5xx). The consumer is expected to
   *  render the generic fallback copy in this case; the error itself is
   *  swallowed into a console.warn so the call site doesn't have to. */
  error: boolean;
  refetch: () => Promise<void>;
}

interface OwnerContactRpcRow {
  display_name?: string | null;
  email?: string | null;
}

/**
 * ViewModel wrapper around `public.get_org_owner_contact(p_org_id)` — a thin
 * RPC that returns the org Owner's display name + email even from a suspended
 * user's perspective (RLS otherwise blocks the suspended user from joining on
 * `profiles`).
 *
 * Used by `/paywall` to render the Owner contact block. On RLS denial or
 * server 5xx the hook returns `{ contact: { display_name: null, email: null },
 * error: true }` and logs the error to the console — the paywall view renders
 * the generic `Contact the org owner to restore access.` copy in that case.
 *
 * @param orgId — active org id; `null` parks the hook in `loading: false`
 *   with empty contact so consumers can call it unconditionally while
 *   `useShellSession` resolves.
 */
export function useOwnerContactVM(orgId: string | null): UseOwnerContactVM {
  const [contact, setContact] = useState<OwnerContact>({
    display_name: null,
    email: null
  });
  const [loading, setLoading] = useState<boolean>(!!orgId);
  const [error, setError] = useState<boolean>(false);

  const load = useCallback(async (id: string) => {
    const { data, error: rpcErr } = await supabase.rpc(
      'get_org_owner_contact',
      { p_org_id: id }
    );
    if (rpcErr) {
      console.warn('[useOwnerContactVM] get_org_owner_contact failed', rpcErr);
      setContact({ display_name: null, email: null });
      setError(true);
      setLoading(false);
      return;
    }
    const rows = (Array.isArray(data) ? data : [data]) as ReadonlyArray<
      OwnerContactRpcRow | null | undefined
    >;
    const row = rows[0] ?? null;
    if (!row) {
      setContact({ display_name: null, email: null });
      setError(false);
      setLoading(false);
      return;
    }
    setContact({
      display_name: row.display_name ?? null,
      email: row.email ?? null
    });
    setError(false);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!orgId) {
      setContact({ display_name: null, email: null });
      setLoading(false);
      setError(false);
      return;
    }
    setLoading(true);
    void load(orgId);
  }, [orgId, load]);

  const refetch = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    await load(orgId);
  }, [orgId, load]);

  return { contact, loading, error, refetch };
}
